'use client';

import { useMemo } from 'react';

import { Typography } from '@workspace/ui/common/Typography/Typography';
import { cn, getButtonVariant } from '@workspace/lib';
import { stripInvisibles } from '@workspace/constants';
import { processHeading, type HeadingType } from '@workspace/lib/heading-utils';
import { Button } from '@workspace/ui/common/Buttons/Button';
import { VariantCard } from './VariantCard';
import { ButtonDTO, VariantItem } from '../product-variant.types';
import { useVariants } from '../useVariants';

type VariantsProps = {
  heading?: HeadingType;
  button?: ButtonDTO;
  buttons?: ButtonDTO[];
  items?: VariantItem[];
  compact?: boolean;
  backgroundColor?: string;
};

const isValidBtn = (b?: ButtonDTO) =>
  !!b && !!b.label && !!b.link && typeof b.link.href === 'string' && b.link.href.length > 0;

export function Variants(props: VariantsProps) {
  const hasPropsData = !!(props.items?.length || props.heading || props.button || props.buttons?.length);
  const { heading: h, buttons: fetchedBtns, items: fetchedItems } = useVariants({ skip: hasPropsData });

  const { text: resolvedHeadingText } = processHeading(props.heading);
  const headingText = resolvedHeadingText || h || 'Product Variants';

  //Check if items have at least one meaningful property
  const items: VariantItem[] = (props.items ?? fetchedItems ?? []).filter(
    (item) =>
      item &&
      (item.heading?.trim() ||
        item.subheading?.trim() ||
        item.productAttribute?.trim() ||
        (item.image?.asset?.url && !item.image.asset.url.includes('placeholder'))),
  );

  const finalButtons: ButtonDTO[] = useMemo(() => {
    const fromProps = props.buttons ?? (props.button ? [props.button] : []);
    const merged = [...(fromProps ?? []), ...(fetchedBtns ?? [])].filter(isValidBtn);
    const seen = new Set<string>();
    return merged.filter((b) => {
      const k = `${b.label}|${b.link!.href}|${(b.style ?? 'PRIMARY').toString().toUpperCase()}`;
      if (seen.has(k)) return false;
      seen.add(k);
      return true;
    });
  }, [props.buttons, props.button, fetchedBtns]);

  const compact = props.compact;

  const sectionClass = `w-full porta-container px-6 py-lg md:px-4xl md:py-3xl ${
    props.backgroundColor ? '' : 'bg-white'
  }`;

  return (
    <div className={cn('variants-block-bg w-full', props.backgroundColor)}>
      <section className={sectionClass}>
        <div className="variant-container mx-auto flex flex-col gap-8 md:gap-6">
          <div className="variant-heading flex flex-col md:flex-row items-start md:items-center md:justify-between gap-8 md:gap-0">
            <Typography variant="h3" className={cn('text-2xl md:text-4xl font-bold text-zinc-900 ')}>
              {headingText}
            </Typography>
            {finalButtons.length > 0 && (
              <div className="variant-button w-full md:w-auto flex flex-col md:flex-row gap-2">
                {finalButtons.map((btn, i) => {
                  const label = stripInvisibles(btn.label || '').trim();
                  return (
                    <Button
                      key={`${btn.label}-${btn.link!.href}-${btn.style}-${i}`}
                      variant={getButtonVariant(btn.style)}
                      url={btn.link!.href}
                      openInNewTab={btn.link!.newTab}
                      className="w-auto max-[540px]:w-full"
                    >
                      {label}
                    </Button>
                  );
                })}
              </div>
            )}
          </div>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-y-5 gap-x-3 md:gap-6">
            {items.map((item, idx) => (
              <div key={`${item.heading || 'variant'}-${item.productAttribute || 'attr'}-${idx}`}>
                <VariantCard item={item} compact={compact} />
              </div>
            ))}
          </div>
        </div>
      </section>
    </div>
  );
}
